/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useState
} from 'react';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import {
  IconButton
} from '../components/buttons/IconButton';
import {
  PrimaryButton
} from '../components/buttons/PrimaryButton';
import {
  TertiaryButton
} from '../components/buttons/TertiaryButton';
import CheckBox from '../components/checkbox/CheckBox';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';
import Render from '../render/Render';
import {dlsColor, dlsLayout, dlsZIndex} from '../theme/theme';
import {IconSize} from '../types/types';
import {HeaderTypography} from '../typography/HeaderTypography';
import SavedColumnContainer from './columns/SavedColumnContainer';
import useEditSavedColumnView from './columns/useEditSavedColumnView';
import {applyColumnOrdering} from './columns/utils';
import ColumnSearch from './ColumnSearch';
import {ColumnDefinitions} from './useColumnDefinitions';

// defines the column chooser client prop type definitions ...
export interface ColumnChooserClient {
  showColumnChooser?: boolean;
  onColumnChooserSave?: (columns: ColDef[]) => void;
  onColumnChooserClose?: () => void
}

// defines the type definitions for custom column chooser styles ...
export type CustomStylesType = {
  variant?: 'permanent' | 'persistent' | 'temporary';
  noTopMargin?: boolean;
  hasMultiColumnPreferences?: boolean;
};

// defines the column chooser prop type definitions ...
export type ColumnChooserProps = {
  columns: ColDef[] | ColumnDefinitions,
  defaults: ColDef[],
  onSave: (columns: ColDef[]) => void,
  onClose: () => void,
  setRefreshRequired?: Function,
  reloadOnVisible?: string[],
  customStyles?: CustomStylesType;
  disableColumnChooserAutoUpdate?: boolean;
  hiddenColumns?: GenericObject;
  hasMultiColumnPreferences?: boolean;
  searchEnabled?: boolean;
  orderEnabled?: boolean;
}

// defines the column chooser style definitions ...
const useStyles = makeStyles(theme => ({
  container : {
    marginTop : ({noTopMargin}: CustomStylesType) => noTopMargin ? 0 : `${dlsLayout('header', 'desktop')}px`,
    width     : ({hasMultiColumnPreferences}: CustomStylesType) => hasMultiColumnPreferences ? 570 : 315,
    maxWidth  : ({hasMultiColumnPreferences}: CustomStylesType) => hasMultiColumnPreferences ? 570 : 315
  },
  header : {
    minHeight      : 66,
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    borderBottom   : `1px solid ${dlsColor('grey5')}`
  },
  close : {
    position : 'absolute',
    top      : 10,
    left     : 10
  },
  controls : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    margin         : '12px 0',
    height         : 40,
    '& >'          : {
      display : 'flex',
      padding : 0
    },
    '& :first-child' : {
      marginRight : 4
    },
    '& :last-child' : {
      marginLeft : 4
    }
  },
  searchBox : {
    padding : '0 30px'
  },
  columns : {
    padding  : '0 40px',
    overflow : 'auto'
  },
  column : {
    padding : '5px'
  },
  footer : {
    margin     : 'auto 20px 80px',
    paddingTop : 16
  },
  drawerZIndex : {
    'z-index' : `${dlsZIndex('columnChooserDrawer')} !important`
  },
  deleteDialogZIndex : {
    'z-index' : `${dlsZIndex('layoutNotificationRoot')} !important`
  }
}));

// provides simple validation by determining if there is at least on column selected ...
const isValid = function(columns: ColDef[]): boolean {
  return Boolean(columns?.find(({hide}: ColDef) => hide === false));
};

// filter columns before displaying within column chooser ...
export const getFilteredColumns = (columns) => {
  return columns?.filter(({headerName, cellRenderer}) => headerName && (cellRenderer !== 'rowMenu'));
};

const ALLOWED_ACTIONS = ['backdropClick', 'escapeKeyDown'];

/**
 * @ColumnChooser
 *
 * The ColumnChooser provides a UI which allows users to select and / or deselect
 * specific Grid Columns for subsequent display within an IonGrid. Checked columns
 * will have their hide property set to false, unchecked columns will have their
 * hide property set to false.
 *
 * ```typescript
 *
 *    <Hidden hide='mobile'>
 *      {showColumnChooser && (
 *        <ColumnChooser columns={columnDefinitions}
 *          onSave={onColumnChooserSave}
 *          onClose={onColumnChooserClose} />
 *      )}
 *    </Hidden>
 * ```
 *
 * @see IonGrid
 */
export const ColumnChooser = ({
  columns,
  defaults,
  onSave,
  onClose,
  customStyles,
  setRefreshRequired,
  hiddenColumns = {},
  reloadOnVisible = [],
  searchEnabled = false,
  orderEnabled = false,
  hasMultiColumnPreferences = false,
  disableColumnChooserAutoUpdate = false
}: ColumnChooserProps) => {
  const classes                                               = useStyles(customStyles);
  const pageName                                              = PageTitle.devices;
  const {isVisible, onRemove, timeout}                        = useAnimatedMount();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen]           = useState(false);
  const [isDefaultOrAllColumn, setIsDefaultOrAllColumn]       = useState(false);
  const [isCheckBoxUpdated, setIsCheckBoxUpdated]             = useState(false);
  const [isSavedColumnChanged, setIsSavedColumnChanged]       = useState(false);
  const [isUnsavedColumn, setIsUnsavedColumn]                 = useState(false);
  const [updatedSavedColumn, setUpdatedSavedColumn]           = useState<string>(null);
  const [selectedSavedColumnName, setSelectedSavedColumnName] = useState<string>(null);
  const [searchTerm, setSearchTerm]                           = useState('');
  const {onEditSavedColumns}                                  = useEditSavedColumnView({pageName});
  const [updates, setUpdates]                                 = useState<ColDef[]>(
    applyColumnOrdering(getFilteredColumns(columns), orderEnabled)
  );

  /**
   * Pass as a callback function to SavedColumList component to check
   * if DeletePrompt is open or not and get a boolean data and update
   * the isDeleteDialogOpen state.
   */
  const checkIsDeleteDialogOpen = useCallback((isOpen = false) => {
    setIsDeleteDialogOpen(isOpen);
  }, []);

  /**
   * Invoke when the user chehages the saved column view name or columns ...
   */
  const getUpdatedColumnView = useCallback((viewName: string, updatedColumns: string) => {
    setSelectedSavedColumnName(viewName);
    setUpdatedSavedColumn(updatedColumns);
  }, []);

  const updateColumn = function(updated: ColDef, checked: boolean): void {
    const current = deepCopy(updates);
    const column  = current.find(({field}) => field === updated.field);
    column.hide   = !checked;
    if (checked && reloadOnVisible?.length && !!reloadOnVisible.find(field => isEqual(field, updated.field))) {
      setRefreshRequired?.(true);
    }
    setUpdates(applyColumnOrdering(current, orderEnabled));
    !disableColumnChooserAutoUpdate && onSave(current);
    if (hasMultiColumnPreferences) {
      setIsDefaultOrAllColumn(false);
      setIsCheckBoxUpdated(true);
    }
  };

  /**
   * Invoked when the user clicks the select / deselect all buttons, at which point
   * all columns are either checked or unchecked, resulting in their corresponding
   * "hide" property being set accordingly.
   */
  const onToggleSelectAll = function(select = true) {
    return function() {
      const current = updates.map(column => {
        return {
          ...column,
          hide : hiddenColumns[column.field] ? true : !select
        };
      });
      setUpdates(applyColumnOrdering(current, orderEnabled));
      if (reloadOnVisible?.length) {
        setRefreshRequired?.(true);
      }
      select && onSave(current);
      if (hasMultiColumnPreferences && select) {
        setIsDefaultOrAllColumn(true);
        setIsCheckBoxUpdated(false);
      }
    };
  };

  /**
   * Invoked when the user chooses to save the column their updated columns selections.
   */
  const onReset = function(): void {
    const update = deepCopy(defaults)?.map((column: ColDef) => {
      column.sort = column.sort || null;
      column.suppressMenu = !column.filter;
      return column;
    });
    setUpdates(applyColumnOrdering(update, orderEnabled));
    onSave(update);
    if (hasMultiColumnPreferences) {
      setIsDefaultOrAllColumn(true);
      setIsCheckBoxUpdated(false);
    }
  };

  /**
   * Invoke when user choose to close the Column chooser drawer ...
   */
  const handleCloseColumnChooser = useCallback(function(reason?: string): void {
    const allowClose = ALLOWED_ACTIONS?.indexOf(reason) > -1 ? isValid(updates) : true;
    disableColumnChooserAutoUpdate && onSave(updates);
    if (allowClose) {
      onRemove(onClose);
      setIsSavedColumnChanged(false);
    }
  }, [updates, onClose, disableColumnChooserAutoUpdate]);

  /**
   * Invoked when the user chooses to close the column chooser ...
   */
  const onCloseColumnChooser = useCallback(function(e, reason?: string): void {
    if (hasMultiColumnPreferences &&
      isSavedColumnChanged &&
      isValid(updates) &&
      selectedSavedColumnName &&
      !isDefaultOrAllColumn
    ) {
      setIsUnsavedColumn(true);
      return;
    }
    handleCloseColumnChooser(reason);
  }, [
    updates,
    onClose,
    disableColumnChooserAutoUpdate,
    isSavedColumnChanged,
    selectedSavedColumnName,
    isDefaultOrAllColumn
  ]);

  /**
   * Invoke When the uer choose to click on Done button.
   * It Update te saved column with selected column if hasMultiColumnPreferences and
   * isSavedColumnChanged value is true and close the Column chooser drawer.
   * Otherwise it only close the Column chooser drawer.
   */
  const onSaveColumnChooser = useCallback(function(e, reason?: string): void {
    if (hasMultiColumnPreferences && isSavedColumnChanged && !isDefaultOrAllColumn) {
      onEditSavedColumns(selectedSavedColumnName, updatedSavedColumn);
    }
    handleCloseColumnChooser(reason);
  }, [
    updates,
    onClose,
    disableColumnChooserAutoUpdate,
    isSavedColumnChanged,
    selectedSavedColumnName,
    updatedSavedColumn,
    isDefaultOrAllColumn
  ]);

  const isShowColumn = (column: ColDef) => {
    return !hiddenColumns[column.field];
  };

  /**
   * Invoke when the user choose to click on Ok button of UnsavedColumn warning dialog ..
   */
  const onProceedClose = useCallback(function(): void {
    setIsUnsavedColumn(false);
  }, [onClose, onCloseColumnChooser]);

  /**
   * Invoked when the user chooses to select a saved column view.
   */
  const onSelectColumnList = function(selectedColumns: string): void {
    const parsed = safeJSONParse(selectedColumns);
    if (parsed?.length) {
      const map = {};
      // pre-process parsed columns into a map for O(1) access ...
      parsed?.forEach((column) => {
        map[column.col_id] = column;
      });
      const result = updates?.map((column) => {
        const match = map[column.field];
        // update column properties if a matching column is found ...
        if (match) {
          column.width = match.width;
          column.sort  = match.sort;
          column.hide  = false;
        } else {
          column.hide = true;
          column.sort = null;
        }
        return column;
      });
      setUpdates(applyColumnOrdering(result, orderEnabled));
      onSave(result);
      setIsDefaultOrAllColumn(false);
    }
  };

  /**
   * Filters the column based on the search query. It checks if the
   * search query is a substring of headerName field.
   */
  const filteredColumns = searchEnabled ? (updates?.filter((update) => {
    return (update?.headerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
  }) || updates) : updates;

  return (
    <Drawer anchor='right'
      open={isVisible}
      variant={customStyles?.variant || 'temporary'}
      onClose={onCloseColumnChooser}
      transitionDuration={timeout}
      classes={{
        modal : !isDeleteDialogOpen ? classes.drawerZIndex : classes.deleteDialogZIndex
      }}
      PaperProps={{
        classes : {root: classes.container}
      }}>
      <header className={classes.header}>
        <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} />}
          className={classes.close}
          disabled={!isValid(updates)}
          onClick={onCloseColumnChooser} />
        <HeaderTypography variant='h2'>
          {translate('common:columnChooser.title')}
        </HeaderTypography>
      </header>
      <section className={classes.controls}>
        <TertiaryButton fontSize='12px' onClick={onToggleSelectAll(true)}>
          {translate('common:columnChooser.select')}
        </TertiaryButton>
        <TertiaryButton fontSize='12px' onClick={onToggleSelectAll(false)}>
          {translate('common:columnChooser.clear')}
        </TertiaryButton>
        <TertiaryButton fontSize='12px' onClick={onReset}>
          {translate('common:columnChooser.reset')}
        </TertiaryButton>
        {hasMultiColumnPreferences && (
          <SavedColumnContainer columns={updates}
            defaults={defaults}
            onSelectColumnList={onSelectColumnList}
            onReset={onReset}
            isDefaultOrAllColumn={isDefaultOrAllColumn}
            isCheckBoxUpdated={isCheckBoxUpdated}
            getUpdatedColumnView={getUpdatedColumnView}
            setIsSavedColumnChanged={setIsSavedColumnChanged}
            checkIsDeleteDialogOpen={checkIsDeleteDialogOpen} />
        )}
      </section>
      <Render.If condition={searchEnabled}>
        <ColumnSearch setSearchTerm={setSearchTerm} />
      </Render.If>
      <article className={classes.columns}>
        {filteredColumns?.map((column: ColDef) => (
          isShowColumn(column) && (
            <div className={classes.column} key={column.field}>
              <CheckBox label={column.headerName}
                checked={!column.hide}
                onChange={function({target:{checked}}: ChangeEvent<HTMLInputElement>) {
                  updateColumn(column, checked);
                }} />
            </div>
          )
        ))}
      </article>
      <footer className={classes.footer}>
        <PrimaryButton fullWidth
          onClick={onSaveColumnChooser}
          disabled={!isValid(updates)}>
          {translate('common:columnChooser.done')}
        </PrimaryButton>
      </footer>
      {isUnsavedColumn && (<UserNotificationDialog show
        capitalize
        zIndex={6000}
        onAction={onProceedClose}
        actionLabel={translate('common:ok')}
        title={translate('common:columnChooser.unsavedColumnWarningDialog.warningTitle')}
        message={translate('common:columnChooser.unsavedColumnWarningDialog.warningMessage')} />)}
    </Drawer>
  );
};

export default ColumnChooser;
